import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Truck, Download, Send, CheckCircle2, MapPin, User2, Phone, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";


function TruckStatusBadge({ status }) {
  const map = {
    Planning: "bg-amber-50 text-amber-700 border-amber-200",
    Loaded: "bg-blue-50 text-blue-700 border-blue-200",
    Dispatched: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return <span className={`text-xs px-2 py-0.5 rounded-md border ${map[status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>{status}</span>;
}

function num(n) { return Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }); }


export default function TruckDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canAct = ["super_admin", "admin", "dispatch", "operations"].includes(user?.role);

  const [truck, setTruck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [available, setAvailable] = useState([]);
  const [picked, setPicked] = useState(new Set());

  async function reload() {
    const { data } = await api.get(`/trucks/${id}`);
    setTruck(data);
  }
  useEffect(() => { reload(); }, [id]);

  async function openAddOrders() {
    const { data } = await api.get("/orders", { params: { status: "Production Completed" } });
    setAvailable(data || []);
    setPicked(new Set());
    setAddOpen(true);
  }

  async function assignPicked() {
    if (picked.size === 0) return toast.error("Select at least one order");
    setBusy(true);
    try {
      await api.post(`/trucks/${id}/assign-order`, { order_ids: Array.from(picked) });
      toast.success(`${picked.size} order(s) added`);
      setAddOpen(false);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  async function removeOrder(oid) {
    if (!confirm("Remove this order from the truck? It will go back to Production Completed.")) return;
    setBusy(true);
    try {
      await api.post(`/trucks/${id}/remove-order`, { order_ids: [oid] });
      toast.success("Order removed");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  async function markLoaded() {
    setBusy(true);
    try {
      await api.post(`/trucks/${id}/load`);
      toast.success("Truck marked Loaded");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  async function markDispatched() {
    if (!confirm("Dispatch this truck? All assigned orders will be marked Dispatched.")) return;
    setBusy(true);
    try {
      await api.post(`/trucks/${id}/dispatch`);
      toast.success("Truck dispatched");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  }

  async function downloadLoadSheet() {
    const token = localStorage.getItem("oms_token");
    try {
      const res = await fetch(`${API}/trucks/${id}/loadsheet/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${truck?.truck_label || "truck"}_loadsheet.pdf`;
      a.click();
      toast.success("Load sheet downloaded");
    } catch {
      toast.error("Failed to download");
    }
  }

  // SKU-wise loading aggregation across orders
  const skuRollup = useMemo(() => {
    if (!truck?.orders) return [];
    const map = new Map();
    for (const o of truck.orders) {
      for (const it of (o.items || [])) {
        const key = it.sku || it.name;
        if (!map.has(key)) map.set(key, { sku: it.sku, name: it.name, total: 0, weight: 0 });
        const r = map.get(key);
        r.total += Number(it.dispatch_quantity || it.quantity || 0);
        r.weight += Number(it.line_weight || 0);
      }
    }
    return Array.from(map.values()).sort((a, b) => (a.sku || "").localeCompare(b.sku || ""));
  }, [truck]);

  if (!truck) return <div className="p-6 text-slate-500">Loading…</div>;

  const isPlanning = truck.status === "Planning";
  const isLoaded = truck.status === "Loaded";
  const isDispatched = truck.status === "Dispatched";

  return (
    <div className="space-y-6" data-testid="truck-detail-page">
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={() => nav("/dispatch")} data-testid="truck-back-btn"><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="heading text-2xl font-bold text-slate-900 mono">{truck.vehicle_number}</h1>
            <TruckStatusBadge status={truck.status} />
            <span className="text-xs text-slate-500">{truck.truck_label}</span>
          </div>
          <div className="text-sm text-slate-500 mt-0.5">Plan date: {fmtDate(truck.plan_date)}</div>
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="truck-actions">
          <Button variant="outline" size="sm" onClick={downloadLoadSheet} data-testid="truck-download-loadsheet"><Download className="h-4 w-4 mr-1" /> Load Sheet</Button>
          {canAct && isPlanning && (
            <Button variant="outline" size="sm" onClick={openAddOrders} data-testid="truck-add-orders"><Plus className="h-4 w-4 mr-1" /> Add Orders</Button>
          )}
          {canAct && isPlanning && (
            <Button size="sm" onClick={markLoaded} disabled={busy || (truck.assigned_order_ids || []).length === 0} data-testid="truck-mark-loaded"><CheckCircle2 className="h-4 w-4 mr-1" /> Mark Loaded</Button>
          )}
          {canAct && (isPlanning || isLoaded) && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={markDispatched} disabled={busy || (truck.assigned_order_ids || []).length === 0} data-testid="truck-dispatch-btn"><Send className="h-4 w-4 mr-1" /> Mark Dispatched</Button>
          )}
        </div>
      </div>

      {/* Truck info grid */}
      <Card className="p-5" data-testid="truck-info">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div><div className="text-xs text-slate-500">Driver</div><div className="font-medium flex items-center gap-1"><User2 className="h-3 w-3" /> {truck.driver_name || "-"}</div></div>
          <div><div className="text-xs text-slate-500">Phone</div><div className="font-medium flex items-center gap-1"><Phone className="h-3 w-3" /> {truck.driver_phone || "-"}</div></div>
          <div><div className="text-xs text-slate-500">Transporter</div><div className="font-medium flex items-center gap-1"><Truck className="h-3 w-3" /> {truck.transporter || "-"}</div></div>
          <div className="md:col-span-2"><div className="text-xs text-slate-500">Route</div><div className="font-medium flex items-center gap-1"><MapPin className="h-3 w-3" /> {truck.route || "-"}</div></div>
          <div><div className="text-xs text-slate-500">Created</div><div className="font-medium">{fmtDateTime(truck.created_at)}</div></div>
          {truck.loaded_at && <div><div className="text-xs text-slate-500">Loaded At</div><div className="font-medium">{fmtDateTime(truck.loaded_at)}</div></div>}
          {truck.dispatched_at && <div><div className="text-xs text-slate-500">Dispatched At</div><div className="font-medium">{fmtDateTime(truck.dispatched_at)}</div></div>}
        </div>
      </Card>

      {/* Totals */}
      <div className="grid grid-cols-3 gap-3" data-testid="truck-totals">
        <Card className="p-3.5"><div className="text-[10px] uppercase tracking-wider text-slate-500">PFIs</div><div className="text-[22px] font-bold tabular-nums leading-none mt-0.5">{(truck.assigned_order_ids || []).length}</div></Card>
        <Card className="p-3.5"><div className="text-[10px] uppercase tracking-wider text-slate-500">Total Boxes</div><div className="text-[22px] font-bold tabular-nums leading-none mt-0.5">{num(truck.total_boxes)}</div></Card>
        <Card className="p-3.5"><div className="text-[10px] uppercase tracking-wider text-slate-500">Total Weight</div><div className="text-[22px] font-bold tabular-nums leading-none mt-0.5">{num(truck.total_weight)}<span className="text-xs font-normal text-slate-500"> kg</span></div></Card>
      </div>

      {/* Assigned PFIs list — distributor-wise */}
      <Card className="overflow-hidden" data-testid="truck-orders">
        <div className="p-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <div className="text-sm font-semibold">Assigned PFIs ({(truck.orders || []).length})</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-white border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="p-3 text-left">PFI</th>
              <th className="p-3 text-left">Distributor</th>
              <th className="p-3 text-left">Area</th>
              <th className="p-3 text-right">Boxes</th>
              <th className="p-3 text-right">Weight</th>
              <th className="p-3">Status</th>
              {canAct && !isDispatched && <th className="p-3 w-12"></th>}
            </tr>
          </thead>
          <tbody>
            {(truck.orders || []).length === 0 && (
              <tr><td colSpan={canAct && !isDispatched ? 7 : 6} className="p-6 text-center text-slate-500" data-testid="truck-orders-empty">No orders assigned yet.</td></tr>
            )}
            {(truck.orders || []).map((o) => (
              <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`truck-order-row-${o.pfi_number}`}>
                <td className="p-3"><a className="font-mono text-xs text-blue-600 hover:underline cursor-pointer" onClick={() => nav(`/orders/${o.id}`)}>{o.pfi_number}</a></td>
                <td className="p-3">{o.distributor_name}</td>
                <td className="p-3 text-slate-600 text-xs">{o.area || "-"}</td>
                <td className="p-3 text-right tabular-nums">{num(o.total_dispatch_quantity || o.total_quantity)}</td>
                <td className="p-3 text-right tabular-nums">{num(o.total_weight)}</td>
                <td className="p-3"><StatusBadge status={o.status} /></td>
                {canAct && !isDispatched && (
                  <td className="p-3">
                    <Button variant="ghost" size="icon" onClick={() => removeOrder(o.id)} disabled={busy} data-testid={`truck-remove-${o.pfi_number}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* SKU-wise loading list */}
      {skuRollup.length > 0 && (
        <Card className="overflow-hidden" data-testid="truck-sku-rollup">
          <div className="p-3 border-b border-slate-200 bg-slate-50 text-sm font-semibold">SKU-wise Loading Summary</div>
          <table className="w-full text-sm">
            <thead className="bg-white border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="p-3 text-left">SKU</th>
                <th className="p-3 text-left">Product</th>
                <th className="p-3 text-right">Boxes</th>
                <th className="p-3 text-right">Weight (kg)</th>
              </tr>
            </thead>
            <tbody>
              {skuRollup.map((r) => (
                <tr key={r.sku} className="border-b border-slate-100">
                  <td className="p-3 font-mono text-xs">{r.sku}</td>
                  <td className="p-3">{r.name}</td>
                  <td className="p-3 text-right tabular-nums">{num(r.total)}</td>
                  <td className="p-3 text-right tabular-nums">{num(r.weight)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Add Orders Modal */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl" data-testid="add-orders-modal">
          <DialogHeader><DialogTitle>Add Orders to Truck</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-auto border border-slate-200 rounded-md">
            {available.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">No Production-Completed orders available.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
                  <tr><th className="p-2 w-8"></th><th className="p-2 text-left">PFI</th><th className="p-2 text-left">Distributor</th><th className="p-2 text-right">Boxes</th><th className="p-2 text-right">Weight</th></tr>
                </thead>
                <tbody>
                  {available.map((o) => (
                    <tr key={o.id} className="border-t border-slate-100">
                      <td className="p-2"><Checkbox checked={picked.has(o.id)} onCheckedChange={() => { const s = new Set(picked); s.has(o.id) ? s.delete(o.id) : s.add(o.id); setPicked(s); }} data-testid={`add-pick-${o.pfi_number}`} /></td>
                      <td className="p-2 font-mono text-xs">{o.pfi_number}</td>
                      <td className="p-2">{o.distributor_name}</td>
                      <td className="p-2 text-right tabular-nums">{num(o.total_dispatch_quantity || o.total_quantity)}</td>
                      <td className="p-2 text-right tabular-nums">{num(o.total_weight)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={assignPicked} disabled={busy || picked.size === 0} data-testid="add-orders-submit">Add {picked.size} Order(s)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
